import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button, Textarea } from '@/components/ds';
import { useFormatters } from '@/hooks/useFormatters';
import type { TicketAttachment, TicketComment } from '@/api/feedback.api';

/**
 * The two-way conversation on a ticket.
 *
 * Rendered as a description list rather than a chat of floating bubbles, for
 * two reasons. A screen reader reading this out needs "Dentacares team, two
 * days ago, we can reproduce this" as one associated unit, which `<dt>`/`<dd>`
 * gives for free. And side alignment alone cannot carry who said what — that is
 * the same mistake as colour-only status — so every entry names its author in
 * text, and the alignment is decoration on top.
 *
 * Alignment uses `ms/me` logical properties, so the clinic's own replies sit on
 * the correct side in Arabic without a second layout.
 */
export function TicketThread({
  comments,
  attachments,
  onReply,
  canReply,
}: {
  comments: TicketComment[];
  attachments: TicketAttachment[];
  onReply: (body: string) => Promise<void>;
  canReply: boolean;
}) {
  const { t } = useTranslation();
  const { dateTime, relative } = useFormatters();

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      await onReply(draft.trim());
      setDraft('');
    } catch {
      setError(t('feedback.errors.replyFailed'));
    } finally {
      setSending(false);
    }
  };

  const attachmentsFor = (commentId: number) =>
    attachments.filter((file) => file.commentId === commentId);

  return (
    <section aria-label={t('feedback.thread.title')} className="space-y-4">
      <h2 className="text-h3 text-foreground">{t('feedback.thread.title')}</h2>

      {comments.length === 0 ? (
        <p className="rounded-lg bg-surface-1 px-4 py-6 text-center text-small text-muted">
          {t('feedback.thread.empty')}
        </p>
      ) : (
        <dl className="space-y-3">
          {comments.map((comment) => {
            const fromTeam = comment.authorKind !== 'clinic';
            const who = fromTeam
              ? t('feedback.thread.team')
              : (comment.author?.name ?? t('feedback.thread.you'));

            return (
              <div
                key={comment.id}
                className={cn(
                  'max-w-[85%] rounded-lg border p-3',
                  fromTeam
                    ? 'me-auto border-border-subtle bg-surface-1'
                    : 'ms-auto border-primary-200 bg-primary-50',
                )}
              >
                <dt className="mb-1 flex flex-wrap items-baseline gap-x-2">
                  <span className="text-small font-semibold text-foreground">
                    {who}
                  </span>
                  {/* Exact time in the tooltip, relative time on screen: one
                      answers "when", the other answers "is this stale". */}
                  <time
                    dateTime={comment.createdAt}
                    title={dateTime(comment.createdAt)}
                    className="text-micro text-muted"
                  >
                    {relative(comment.createdAt)}
                  </time>
                </dt>
                <dd className="whitespace-pre-wrap text-body text-foreground">
                  {comment.body}
                </dd>

                {attachmentsFor(comment.id).length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {attachmentsFor(comment.id).map((file) => (
                      <li key={file.id}>
                        <ThreadAttachment file={file} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </dl>
      )}

      {canReply && (
        <div className="space-y-2 border-t border-border-subtle pt-4">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('feedback.thread.placeholder')}
            aria-label={t('feedback.thread.placeholder')}
            rows={3}
            disabled={sending}
          />
          {error && (
            <p role="alert" className="text-small text-danger-600">
              {error}
            </p>
          )}
          <div className="flex justify-end">
            <Button
              onPress={send}
              isDisabled={!draft.trim() || sending}
              isLoading={sending}
              loadingText={t('feedback.thread.sending')}
              startIcon={<Send className="size-4" />}
            >
              {t('feedback.thread.send')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * One attachment.
 *
 * Images render inline because everything stored has been re-encoded through
 * sharp server-side, so the bytes are something sharp produced. Anything else
 * is a link, never an inline frame.
 */
export function ThreadAttachment({ file }: { file: TicketAttachment }) {
  const isImage = file.mime.startsWith('image/');

  if (isImage && file.url) {
    return (
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block overflow-hidden rounded-md border border-border-subtle outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <img
          src={file.url}
          alt={file.label ?? ''}
          loading="lazy"
          className="size-20 object-cover"
        />
      </a>
    );
  }

  return (
    <a
      href={file.url ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-2 py-1 text-micro text-foreground outline-none hover:bg-surface-1 focus-visible:ring-2 focus-visible:ring-primary/60"
    >
      {file.label ?? file.mime}
    </a>
  );
}
